// src/services/customerService.js
import axiosInstance from "../config/axiosInstance";

const BASE_URL = "/customers";

/**
 * Obtiene una lista paginada de clientes con búsqueda opcional.
 * @param {number} page - Número de página (1..N)
 * @param {number} size - Tamaño de página
 * @param {string|null} searchTerm - Término de búsqueda (opcional)
 * @returns {Promise<ApiResponse<PaginatedResult<CustomerListItemDto>>>}
 */
export const getCustomersPaged = async (
  page = 1,
  size = 10,
  searchTerm = null
) => {
  const { data } = await axiosInstance.get(
    `${BASE_URL}/paged?pageIndex=${page}&pageSize=${size}&searchTerm=${
      searchTerm ?? ""
    }`
  );
  return data;
};

/**
 * Obtiene el detalle de un cliente por ID.
 * @param {string} id - ID del cliente (GUID)
 * @returns {Promise<ApiResponse<CustomerDetailDto>>}
 */
export const getCustomerById = async (id) => {
  const { data } = await axiosInstance.get(`${BASE_URL}/${id}`);
  return data;
};

/**
 * Crea un nuevo cliente (incluye creación del SecurityUser y asignación de roles).
 * @param {CustomerCreateDto} payload - Datos para crear el cliente
 * @returns {Promise<ApiResponse<string>>} // Guid
 */
export const createCustomer = async (payload) => {
  const { data } = await axiosInstance.post(BASE_URL, payload);
  return data;
};

/**
 * Actualiza un cliente existente (y su SecurityUser asociado).
 * @param {string} id - ID del cliente
 * @param {CustomerUpdateDto} payload - Datos a actualizar
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const updateCustomer = async (id, payload) => {
  const { data } = await axiosInstance.put(`${BASE_URL}/${id}`, payload);
  return data;
};

/**
 * Soft delete de un cliente por ID.
 * @param {string} id - ID del cliente
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const deleteCustomer = async (id) => {
  const { data } = await axiosInstance.delete(`${BASE_URL}/${id}`);
  return data;
};

/**
 * Obtiene una lista paginada de clientes eliminados lógicamente,
 * con opción de búsqueda por nombre, correo, documento o empresa.
 * @param {number} page - Número de página (1..N)
 * @param {number} size - Tamaño de página
 * @param {string|null} searchTerm - Término de búsqueda (opcional)
 * @returns {Promise<ApiResponse<PaginatedResult<CustomerDeletedListItemDto>>>}
 */
export const getDeletedCustomersPaged = async (
  page = 1,
  size = 10,
  searchTerm = null
) => {
  const { data } = await axiosInstance.get(
    `${BASE_URL}/deleted?pageIndex=${page}&pageSize=${size}&searchTerm=${
      searchTerm ?? ""
    }`
  );
  return data;
};

/**
 * Restaura un cliente previamente eliminado lógicamente.
 * @param {string} id - Guid del cliente
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const restoreCustomer = async (id) => {
  const { data } = await axiosInstance.put(`${BASE_URL}/${id}/restore`);
  return data;
};

/**
 * Obtiene un listado ligero de clientes activos
 * para poblar combos (selects) en formularios.
 * @returns {Promise<ApiResponse<CustomerLookupItemDto[]>>}
 */
export const getActiveCustomersLookup = async () => {
  const { data } = await axiosInstance.get(`${BASE_URL}/active-lookup`);
  return data;
};
